import React, { useState, useCallback } from 'react';
import {
  Paper,
  TextField,
  Button,
  Box,
  Typography,
  InputAdornment,
  Divider,
  Tooltip,
  Fade,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import TransformIcon from '@mui/icons-material/Transform';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSnackbar } from 'notistack';
import Stats from './Stats';

export default function LinkConverter() {
  const [inputUrl, setInputUrl] = useState('');
  const [outputUrl, setOutputUrl] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  
  // Load stats from localStorage
  const getInitialStats = () => {
    try {
      const saved = localStorage.getItem('linkConverterStats');
      return saved ? JSON.parse(saved) : { convertCount: 0, copyCount: 0 };
    } catch {
      return { convertCount: 0, copyCount: 0 };
    }
  };
  
  const [stats, setStats] = useState(getInitialStats);

  const saveStats = useCallback((newStats) => {
    setStats(newStats);
    try {
      localStorage.setItem('linkConverterStats', JSON.stringify(newStats));
    } catch (e) {
      console.error('Failed to save stats:', e);
    }
  }, []);

  const convertLink = (url) => {
    const trimmedUrl = url.trim();
    
    if (!trimmedUrl) {
      throw new Error('กรุณาวางลิงก์ที่ต้องการแปลง');
    }
    
    const urlObj = new URL(trimmedUrl);
    
    if (!trimmedUrl.includes('elearning.chonburi.spu.ac.th')) {
      throw new Error('ลิงก์ไม่ถูกต้อง กรุณาใช้ลิงก์จาก elearning.chonburi.spu.ac.th');
    }
    
    const params = new URLSearchParams(urlObj.search);
    const rcid = params.get('rcid');
    const sid = params.get('sid');
    
    if (!rcid || !sid) {
      throw new Error('ไม่พบพารามิเตอร์ที่จำเป็น (rcid หรือ sid)');
    }
    
    return `https://elearning.chonburi.spu.ac.th/getAnswer.php?mod=viewAnswer&rcid=&rcid=${rcid}&sid=${sid}`;
  };

  const handleConvert = useCallback(() => {
    setError('');
    setShowResult(false);
    setCopied(false);

    if (!inputUrl.trim()) {
      setError('กรุณาวางลิงก์ที่ต้องการแปลง');
      enqueueSnackbar('กรุณาวางลิงก์ที่ต้องการแปลง', { variant: 'error' });
      return;
    }

    try {
      const converted = convertLink(inputUrl);
      setOutputUrl(converted);
      setShowResult(true);
      const newStats = { ...stats, convertCount: stats.convertCount + 1 };
      saveStats(newStats);
      enqueueSnackbar('แปลงลิงก์สำเร็จ!', { variant: 'success' });
    } catch (err) {
      setError(err.message);
      enqueueSnackbar(err.message, { variant: 'error' });
    }
  }, [inputUrl, stats, saveStats, enqueueSnackbar]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(outputUrl);
      setCopied(true);
      const newStats = { ...stats, copyCount: stats.copyCount + 1 };
      saveStats(newStats);
      enqueueSnackbar('คัดลอกลิงก์แล้ว!', { variant: 'success' });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      enqueueSnackbar('ไม่สามารถคัดลอกได้', { variant: 'error' });
    }
  }, [outputUrl, stats, saveStats, enqueueSnackbar]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      handleConvert();
    }
  }, [handleConvert]);

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          mb: 3,
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              color: 'primary.main',
              fontWeight: 600,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <LinkIcon sx={{ color: 'secondary.main' }} />
            วางลิงก์ที่ต้องการแปลง
          </Typography>

          <TextField
            fullWidth
            variant="outlined"
            placeholder="https://elearning.chonburi.spu.ac.th/resourcecenter.php?..."
            value={inputUrl}
            onChange={(e) => setInputUrl(e.target.value)}
            onKeyPress={handleKeyPress}
            error={!!error}
            helperText={error}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LinkIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Button
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            onClick={handleConvert}
            startIcon={<TransformIcon />}
            sx={{ py: 1.5 }}
          >
            แปลงลิงก์
          </Button>
        </Box>

        <Fade in={showResult}>
          <Box>
            <Divider sx={{ my: 3, borderColor: 'secondary.main', borderStyle: 'dashed' }} />
            
            <Typography
              variant="h6"
              sx={{
                color: 'success.main',
                fontWeight: 600,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CheckCircleIcon />
              ลิงก์ที่แปลงแล้ว
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                variant="outlined"
                value={outputUrl}
                InputProps={{
                  readOnly: true,
                  sx: {
                    fontFamily: 'monospace',
                    bgcolor: 'grey.50',
                  },
                }}
              />
              <Tooltip title={copied ? 'คัดลอกแล้ว!' : 'คัดลอกลิงก์'}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleCopy}
                  sx={{
                    minWidth: 120,
                    bgcolor: copied ? 'success.main' : 'secondary.main',
                    '&:hover': {
                      bgcolor: copied ? 'success.dark' : 'secondary.dark',
                    },
                  }}
                >
                  {copied ? (
                    <>
                      <CheckCircleIcon sx={{ mr: 1 }} />
                      คัดลอกแล้ว
                    </>
                  ) : (
                    <>
                      <ContentCopyIcon sx={{ mr: 1 }} />
                      คัดลอก
                    </>
                  )}
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </Fade>
      </Paper>

      <Stats convertCount={stats.convertCount} copyCount={stats.copyCount} />
    </>
  );
}
